"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BrowserLogger = void 0;
const redact_1 = require("./redact");
/** custom log levels with priority (lower = higher priority) */
const logLevels = {
    isolate: 0,
    error: 1,
    warn: 2,
    info: 3,
};
/**
 * Browser-compatible logger that mirrors the Winston-based Logger interface.
 * Uses native console methods instead of Node.js streams.
 */
class BrowserLogger {
    /**
     * Class Constructor
     *
     * @param transportType - The type of transport (ignored in browser, always uses console)
     * @param transportOptions - (Optional) The transport options
     */
    constructor(transportType, transportOptions) {
        this.silent = transportOptions?.silent ?? false;
        this.level = transportOptions?.level ?? "info";
    }
    /**
     * Check if a log level should be output based on current level setting
     */
    shouldLog(level) {
        if (this.silent)
            return false;
        return logLevels[level] <= logLevels[this.level];
    }
    /**
     * Sets silent property of logger
     * @param val - Boolean value for silent property
     */
    setSilent(val) {
        this.silent = val;
    }
    /**
     * gets value of silent property of logger
     * @returns value of silent property of logger
     */
    isSilent() {
        return this.silent;
    }
    /**
     * Redacts a message string without throwing. Falls back to the original
     * value if redaction fails, since logging must never break the caller.
     */
    safeMessage(message) {
        try {
            return typeof message === "string" ? (0, redact_1.redact)(message) : message;
        }
        catch {
            return message;
        }
    }
    /**
     * Builds the additional console argument list for structured metadata,
     * redacting it first. Returns an empty array when there is no metadata.
     */
    metaArgs(meta) {
        if (meta && Object.keys(meta).length > 0) {
            try {
                return [(0, redact_1.redact)(meta)];
            }
            catch {
                return [];
            }
        }
        return [];
    }
    /**
     * logs a isolate level log
     * @param message - the message you want to log
     * @param meta - optional structured context emitted as a redacted object
     */
    isolate(message, meta) {
        if (this.shouldLog("isolate")) {
            console.log(`[ISOLATE] ${this.safeMessage(message)}`, ...this.metaArgs(meta));
        }
    }
    /**
     * logs a error level log
     * @param message - the message you want to log
     * @param meta - optional structured context emitted as a redacted object
     */
    error(message, meta) {
        if (this.shouldLog("error")) {
            console.error(`[ERROR] ${this.safeMessage(message)}`, ...this.metaArgs(meta));
        }
    }
    /**
     * logs a warn level log
     * @param message - the message you want to log
     * @param meta - optional structured context emitted as a redacted object
     */
    warn(message, meta) {
        if (this.shouldLog("warn")) {
            console.warn(`[WARN] ${this.safeMessage(message)}`, ...this.metaArgs(meta));
        }
    }
    /**
     * logs a info level log
     * @param message - the message you want to log
     * @param meta - optional structured context emitted as a redacted object
     */
    info(message, meta) {
        if (this.shouldLog("info")) {
            console.info(`[INFO] ${this.safeMessage(message)}`, ...this.metaArgs(meta));
        }
    }
    /**
     * logs a log at provided log level
     * @param logLevel - the log level at which to log the message
     * @param message - the message you want to log
     * @param meta - optional structured context emitted as a redacted object
     */
    log(logLevel, message, meta) {
        switch (logLevel) {
            case "isolate":
                this.isolate(message, meta);
                break;
            case "error":
                this.error(message, meta);
                break;
            case "warn":
                this.warn(message, meta);
                break;
            case "info":
                this.info(message, meta);
                break;
            default:
                break;
        }
    }
    /**
     * Compatibility property - returns a mock logger object
     * This exists to maintain API compatibility with the Winston-based Logger
     */
    get logger() {
        return {
            silent: this.silent,
            log: this.log.bind(this),
            error: this.error.bind(this),
            warn: this.warn.bind(this),
            info: this.info.bind(this),
        };
    }
}
exports.BrowserLogger = BrowserLogger;
//# sourceMappingURL=BrowserLogger.js.map